/**
 * @file RightsAnalysisReport.tsx
 * @description 권리 분석 상세 리포트 컴포넌트 (Premium)
 *
 * 주요 기능:
 * 1. 3페이지 구조의 프리미엄 리포트
 * 2. Page 1: 종합 진단 개요 (물건 권리 요약, 종합 등급, 리스크 진단)
 * 3. Page 2: 상세 분석 및 근거 (등기부 분석, 임차인 분석, 배당 시뮬레이션)
 * 4. Page 3: 최종 점검 및 행동 가이드 (특수 권리 점검, 현장 조사, 면책 조항)
 *
 * 핵심 구현 로직:
 * - 권리 안전성 등급 S/A/B/C 계산 (evictionRisk + assumableRightsTotal 종합)
 * - Property 정보 통합 (소재지, 건물/토지, 물건종별)
 * - 배당 시뮬레이션 테이블 (AllocationTable 활용)
 *
 * 브랜드 통합:
 * - Nanum Myeongjo 폰트 사용 (Serif, 프리미엄 리포트용)
 * - 다크모드 지원
 *
 * @dependencies
 * - @/lib/types: Rights, CourtDocsNormalized, Property, AllocationTable
 * - @/lib/domain/auction/utils/property-labels: getPropertyTypeLabel
 * - @/components/shared/ui/table: shadcn 테이블 컴포넌트
 *
 * @see {@link /docs/refactoring/22-simulation-scope-matrix.md} - 현재 권리 리포트 유지 범위
 */

"use client";

import { useEffect, useRef } from "react";
import Lenis from "@studio-freight/lenis";
import {
  Rights,
  CourtDocsNormalized,
  Property,
  AllocationTable,
  PropertyType,
} from "@/lib/core/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/shared/ui/table";
import { getPropertyTypeLabel } from "@/lib/domain/auction/utils/property-labels";
import { getRightTypeLabel } from "@/lib/domain/auction/utils/right-type-labels";
import { formatDateString } from "@/lib/domain/auction/utils/auction-dates";
import { RIGHTS_POLICY_TABLE } from "@/lib/core/policy/rightspolicy";
import { RightType } from "@/lib/core/types/rights";

/**
 * 말소기준권리 유형 (RightType enum 값들)
 */
const BASE_RIGHT_TYPES: RightType[] = [
  RightType.Mortgage,        // 근저당권
  RightType.ProvisionalSeizure, // 가압류
  RightType.ProvisionalRegistry, // 예고등기 (담보가등기)
  RightType.Seizure,         // 압류 (경매개시결정등기 관련)
  RightType.Leasehold,       // 임차권 (전세권)
];

/**
 * 말소기준권리 계산 함수
 * 건물등기와 토지등기에서 5가지 권리유형 중 가장 빠른 날짜를 찾음
 */
function calculateBaseRightDate(
  buildingRights: any[],
  landRights: any[],
  legacyRights: any[] = [],
): string {
  // 건물등기, 토지등기, 그리고 기존 registeredRights 모두 합치기
  const allRights = [...buildingRights, ...landRights, ...legacyRights];

  console.log("[calculateBaseRightDate] === 말소기준권리 계산 시작 ===");
  console.log("[calculateBaseRightDate] 건물등기 권리들:", buildingRights.map(r => `${r.type}: ${r.date}`));
  console.log("[calculateBaseRightDate] 토지등기 권리들:", landRights.map(r => `${r.type}: ${r.date}`));
  console.log("[calculateBaseRightDate] 기존 registeredRights:", legacyRights.map(r => `${r.type}: ${r.date}`));
  console.log("[calculateBaseRightDate] 모든 권리들:", allRights.map(r => `${r.type}: ${r.date}`));
  console.log("[calculateBaseRightDate] 말소기준권리 유형:", BASE_RIGHT_TYPES);

  // 말소기준권리 유형에 해당하는 권리들 필터링
  const baseRights = allRights.filter((right) => {
    if (!right.type || !right.date) return false;

    // RightType enum 값으로 정확한 매칭
    const matches = BASE_RIGHT_TYPES.includes(right.type as RightType);

    if (matches) {
      const label = RIGHTS_POLICY_TABLE[right.type as RightType]?.label || right.type;
      console.log(`[calculateBaseRightDate] 매치된 권리: ${label} (${right.date}) - 타입: ${right.type}`);
    } else {
      const label = RIGHTS_POLICY_TABLE[right.type as RightType]?.label || right.type;
      console.log(`[calculateBaseRightDate] 제외된 권리: ${label} (${right.date}) - 타입: ${right.type}`);
    }

    return matches;
  });

  console.log("[calculateBaseRightDate] 필터링된 말소기준권리들:", baseRights);

  if (baseRights.length === 0) {
    console.log("[calculateBaseRightDate] 말소기준권리가 없음 - 빈 문자열 반환");
    // 말소기준권리가 없으면 빈 문자열 반환
    return "";
  }

  // 날짜로 정렬하여 가장 빠른 날짜 찾기
  const sortedRights = baseRights
    .filter((right) => right.date) // 날짜가 있는 것만
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  console.log("[calculateBaseRightDate] 정렬된 권리들:", sortedRights.map(r => `${r.type}: ${r.date}`));
  const result = sortedRights.length > 0 ? sortedRights[0].date : "";
  console.log("[calculateBaseRightDate] 최종 말소기준권리 날짜:", result);

  return result;
}

export interface RightsAnalysisReportProps {
  rights: Rights;
  courtDocs: CourtDocsNormalized;
  property: Property;
  allocationTable?: AllocationTable;
  userBid?: number;
  isSample?: boolean; // 샘플 리포트 여부 (샘플 리포트는 GA 이벤트 전송 안 함)
}

export function RightsAnalysisReport({
  rights,
  courtDocs,
  property,
  allocationTable,
  userBid,
  isSample = false,
}: RightsAnalysisReportProps) {
  console.group("RightsAnalysisReport Component");
  console.log("Rights data:", {
    assumableRightsTotal: rights.assumableRightsTotal,
    evictionCostEstimated: rights.evictionCostEstimated,
    evictionRisk: rights.evictionRisk,
    riskFlags: rights.riskFlags,
  });
  console.log("Court docs:", {
    caseNumber: courtDocs?.caseNumber,
    registeredRightsCount: courtDocs?.registeredRights?.length || 0,
    occupantsCount: courtDocs?.occupants?.length || 0,
    baseRightDate: courtDocs?.baseRightDate,
  });
  console.log("Property:", {
    address: property.address,
    type: property.type,
    sizeM2: property.sizeM2,
    landSizeM2: property.landSizeM2,
  });
  console.groupEnd();

  // 권리 안전성 등급 계산 함수 (S/A/B/C) - hooks 이전에 정의
  const getRightsSafetyGrade = (
    rights: Rights,
    courtDocs: CourtDocsNormalized,
  ): "S" | "A" | "B" | "C" => {
    // 총 인수 비용 계산 (등기부 권리 + 명도 비용)
    const totalAssumableAmount = rights.assumableRightsTotal + rights.evictionCostEstimated;

    // S: 매우 안전 - 인수 권리 없음, 명도 수월
    if (
      totalAssumableAmount === 0 &&
      rights.evictionRisk <= 1.0 &&
      !(courtDocs.occupants || []).some((occ) => occ.hasCountervailingPower)
    ) {
      return "S";
    }

    // C: 위험 - 특수권리 또는 높은 리스크
    if (
      rights.evictionRisk >= 4.0 ||
      totalAssumableAmount > 50000000 ||
      rights.riskFlags.some(
        (flag) => flag.includes("유치권") || flag.includes("법정지상권"),
      )
    ) {
      return "C";
    }

    // B: 주의 - 대항력 있는 임차인 또는 중간 리스크
    if (
      (courtDocs.occupants || []).some((occ) => occ.hasCountervailingPower) ||
      rights.evictionRisk >= 2.5 ||
      totalAssumableAmount > 20000000
    ) {
      return "B";
    }

    // A: 안전 - 기본적으로 안전하나 소액 추가 비용 가능
    return "A";
  };

  // 이미지 저장 및 스크린샷 방지 (데스크톱 + 모바일)
  // React Hooks 규칙: early return 이전에 호출되어야 함
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // F12, Ctrl+Shift+I (개발자 도구), Ctrl+Shift+J (콘솔), Ctrl+U (소스 보기)
      // PrintScreen 키 감지 시도 (일부 브라우저에서만 작동)
      if (
        e.key === "F12" ||
        e.key === "PrintScreen" ||
        e.keyCode === 44 || // PrintScreen 키코드
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) ||
        (e.ctrlKey && e.key === "U") ||
        (e.ctrlKey && e.key === "S") ||
        (e.ctrlKey && e.shiftKey && e.key === "S") ||
        (e.ctrlKey && e.key === "P") // 인쇄 단축키
      ) {
        e.preventDefault();
        alert("스크린샷 및 인쇄가 제한된 콘텐츠입니다.");
        return false;
      }
    };

    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    // 화면 전환 감지 (데스크톱: Alt+Tab, Win+Tab / 모바일: 앱 전환)
    let lastVisibilityChange = Date.now();
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 화면이 숨겨졌을 때 (스크린샷 도구 사용 가능성)
        console.warn("화면 전환이 감지되었습니다.");
        lastVisibilityChange = Date.now();
      } else {
        // 화면이 다시 보일 때 - 빠른 전환은 스크린샷 가능성
        const timeDiff = Date.now() - lastVisibilityChange;
        if (timeDiff < 1000) {
          // 1초 이내 빠른 전환은 스크린샷 의심
          console.warn("스크린샷이 의심됩니다.");
        }
      }
    };

    // 모바일: 페이지 숨김 감지 (스크린샷 앱 사용 시)
    // pagehide 이벤트 제거: bfcache를 방해하므로 제거 (성능 최적화)

    // 모바일: 화면 방향 변경 감지 (일부 스크린샷 앱 사용 시)
    const handleOrientationChange = () => {
      console.warn("화면 방향이 변경되었습니다.");
    };

    // 포커스 잃음 감지
    const handleBlur = () => {
      console.warn("윈도우 포커스가 변경되었습니다.");
    };

    // 모바일: 터치 이벤트로 특정 제스처 감지 (스크린샷 제스처 시도)
    let touchStartTime = 0;
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();
      if (e.touches.length > 0) {
        touchStartY = e.touches[0].clientY;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchDuration = Date.now() - touchStartTime;
      // 빠른 터치나 특정 제스처는 스크린샷 시도일 수 있음
      if (touchDuration < 100 && e.changedTouches.length > 0) {
        const touchEndY = e.changedTouches[0].clientY;
        const deltaY = Math.abs(touchEndY - touchStartY);
        // 매우 작은 이동은 스크린샷 제스처일 수 있음
        if (deltaY < 10) {
          console.warn("의심스러운 터치 제스처가 감지되었습니다.");
        }
      }
    };

    // 복사 방지
    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    // 선택 방지 강화
    const handleSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // 모바일: 롱프레스 방지 (이미지 저장 시도)
    // 주의: touchmove에서 preventDefault를 호출하면 스크롤이 막히므로 제거
    // 롱프레스 감지는 touchstart/touchend의 시간 차이로 처리

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // pagehide 이벤트 제거: bfcache를 방해하므로 제거 (성능 최적화)
    document.addEventListener("copy", handleCopy);
    document.addEventListener("selectstart", handleSelectStart);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("orientationchange", handleOrientationChange);
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // pagehide 이벤트 제거: bfcache를 방해하므로 제거 (성능 최적화)
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("selectstart", handleSelectStart);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("orientationchange", handleOrientationChange);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);


  // Lenis 스크롤 적용
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;

    // Lenis 인스턴스 생성
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    });

    // 컨테이너에 Lenis 적용
    lenis.scrollTo(0, { immediate: true });

    // requestAnimationFrame을 사용하여 Lenis 업데이트
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // 컴포넌트 언마운트 시 정리
    return () => {
      lenis.destroy();
    };
  }, []);

  // 프리미엄 리포트 조회수 카운팅 (중복 방지) - React Hooks 규칙: early return 이전에 호출
  // 샘플 리포트는 조회수 카운팅 안 함
  useEffect(() => {
    if (isSample) return; // 샘플 리포트는 조회수 카운팅 안 함

    const trackReportView = async () => {
      if (!courtDocs) return;

      // 중복 방지: localStorage에 오늘 날짜 + 리포트 타입 + caseNumber 조합으로 저장
      const today = new Date().toISOString().split("T")[0];
      const reportKey = `premium_report_view_${today}_rights_${courtDocs.caseNumber}`;

      // 이미 조회한 리포트인지 확인
      if (typeof window !== "undefined") {
        const hasViewed = localStorage.getItem(reportKey);
        if (hasViewed) {
          return;
        }

        try {
          const response = await fetch("/api/simulation/report-view", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              reportType: "rights",
              simulationId: courtDocs.caseNumber,
            }),
          });

          if (response.ok) {
            // 조회 완료 표시
            localStorage.setItem(reportKey, "true");
          }
        } catch {
          // 조회수 카운팅 실패 시 조용히 처리
        }
      }
    };

    trackReportView();
  }, [isSample, courtDocs]);

  // 안전성 체크: courtDocs가 없거나 필수 필드가 없으면 기본값 사용
  if (!courtDocs || !courtDocs.registeredRights || !courtDocs.occupants) {
    console.warn("⚠️ CourtDocs 데이터가 불완전합니다. 기본값을 사용합니다.");
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>권리 분석 리포트 데이터를 불러올 수 없습니다.</p>
        <p className="text-sm mt-2">courtDocs 데이터가 필요합니다.</p>
      </div>
    );
  }

  const safetyGrade = getRightsSafetyGrade(rights, courtDocs);

  // 예상 인수금액 계산
  const totalAssumableAmount =
    rights.assumableRightsTotal + rights.evictionCostEstimated;

  // 청구금액 (배당요구 등으로 발생하는 비용)
  const claimAmount = property.claimAmount || 0;

  // 핵심 판단 기준 분석 (안전성 체크)
  const hasCountervailingPower = (courtDocs.occupants || []).some(
    (occ) => occ.hasCountervailingPower,
  );
  const hasDividendRequest = (courtDocs.occupants || []).some(
    (occ) => occ.dividendRequested,
  );

  // 말소기준권리 정보 추출
  // 5가지 권리유형(근저당권, 가압류, 담보가등기, 경매개시결정등기, 전세권) 중
  // 건물등기와 토지등기에서 가장 빠른 날짜를 말소기준권리로 설정

  // 건물등기와 토지등기 권리 가져오기
  const buildingRightsForBase = courtDocs.buildingRegisteredRights ?? [];
  const landRightsForBase = courtDocs.landRegisteredRights ?? [];

  // 말소기준권리 날짜 계산 (5가지 권리유형 중 가장 빠른 날짜)
  const calculatedBaseRightDate = calculateBaseRightDate(
    buildingRightsForBase,
    landRightsForBase,
    courtDocs.registeredRights ?? [], // 기존 registeredRights도 확인
  );

  // 모든 등기부 리스트 합치기 (기존 호환성을 위해)
  const allRightsForBase = (() => {
    if (buildingRightsForBase.length > 0 || landRightsForBase.length > 0) {
      return [...buildingRightsForBase, ...landRightsForBase];
    }
    return courtDocs.registeredRights ?? [];
  })();

  // 계산된 말소기준권리 날짜와 일치하는 권리 찾기
  const baseRight = calculatedBaseRightDate
    ? allRightsForBase.find((r) => r.date === calculatedBaseRightDate)
    : undefined;

  // 최종 말소기준권리 날짜 (물건개요의 계산 로직과 동일하게 적용)
  const baseRightDate = calculatedBaseRightDate || "";
  
  const baseRightCreditor = baseRight?.creditor || "근저당권";
  const baseRightTypeLabel = baseRight
    ? getRightTypeLabel(baseRight.type)
    : "근저당권";

  // 후순위 권리 개수 계산 (말소기준권리 제외)
  // RightsEngine의 extractRightsFromRegistry 로직과 동일하게 모든 등기부 리스트 사용
  const allRightsForSubordinated = (() => {
    const buildingRights = courtDocs.buildingRegisteredRights ?? [];
    const landRights = courtDocs.landRegisteredRights ?? [];
    
    if (buildingRights.length > 0 || landRights.length > 0) {
      return [...buildingRights, ...landRights];
    }
    
    return courtDocs.registeredRights ?? [];
  })();
  
  // 중복 제거: 같은 등기목적과 접수일자로 건물등기와 토지등기가 동시에 등재된 경우 중복 제거
  const seenForSubordinated = new Set<string>();
  const uniqueRightsForSubordinated = allRightsForSubordinated.filter((r) => {
    const key = `${r.creditor}_${r.date}_${r.amount}_${r.type}`;
    if (seenForSubordinated.has(key)) {
      return false;
    }
    seenForSubordinated.add(key);
    return true;
  });
  
  const subordinatedRightsCount =
    uniqueRightsForSubordinated.filter((r) => !r.isBaseRight).length;

  // 대항력 있는 임차인 정보
  const countervailingOccupant = (courtDocs.occupants || []).find(
    (occ) => occ.hasCountervailingPower,
  );

  // 위반건축물 여부 확인 (riskFlags 또는 remarks에서 확인)
  const hasBuildingViolation =
    rights.riskFlags.some(
      (flag) =>
        flag.includes("위반건축물") ||
        flag.includes("위반건축") ||
        flag.includes("건축위반"),
    ) ||
    (courtDocs.remarks &&
      (courtDocs.remarks.includes("위반건축물") ||
        courtDocs.remarks.includes("위반건축") ||
        courtDocs.remarks.includes("건축위반")));

  // 등기부 권리 인수 항목 개수 계산
  // RightsEngine의 extractRightsFromRegistry 로직과 동일하게 모든 등기부 리스트 사용
  const allRightsForCount = (() => {
    const buildingRights = courtDocs.buildingRegisteredRights ?? [];
    const landRights = courtDocs.landRegisteredRights ?? [];
    
    if (buildingRights.length > 0 || landRights.length > 0) {
      return [...buildingRights, ...landRights];
    }
    
    return courtDocs.registeredRights ?? [];
  })();
  
  // 중복 제거: 같은 등기목적과 접수일자로 건물등기와 토지등기가 동시에 등재된 경우 중복 제거
  const seenForCount = new Set<string>();
  const uniqueRightsForCount = allRightsForCount.filter((r) => {
    const key = `${r.creditor}_${r.date}_${r.amount}_${r.type}`;
    if (seenForCount.has(key)) {
      return false;
    }
    seenForCount.add(key);
    return true;
  });
  
  // TabRights.tsx의 getRightInheritanceStatus 로직과 동일하게 적용
  const assumableRightsCount = uniqueRightsForCount.filter((r) => {
    // 말소기준권리는 인수 대상이지만 개수에는 포함하지 않음
    if (r.isBaseRight) {
      return false;
    }

    // 말소기준권리 이후의 권리는 경매로 말소되지 않아 인수 대상
    // 따라서 개수 계산에서 제외하지 않음

    // 말소기준권리 이전의 권리는 breakdown을 확인하여 inheritable 여부 판단
    const breakdownItem = rights.breakdown.find(
      (item) => item.type === r.type,
    );

    if (breakdownItem) {
      return breakdownItem.inheritable || breakdownItem.payout > 0;
    }

    // breakdown에 없으면 정책 테이블 확인
    const policy =
      RIGHTS_POLICY_TABLE[r.type as keyof typeof RIGHTS_POLICY_TABLE];
    return policy?.inheritable ?? false;
  }).length;

  // 분석일 (현재 날짜)
  const analysisDate: string = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // 등급별 색상 및 설명
  const gradeConfig = {
    S: {
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      label: "매우 안전",
      description:
        "인수 권리가 전혀 없고, 명도 또한 매우 수월한 최상급 안전 물건.",
    },
    A: {
      color: "text-green-600",
      bgColor: "bg-green-50",
      label: "안전",
      description:
        "권리상 하자는 없으나, 관리비 미납 등 소액의 추가 비용 발생 가능성 있음.",
    },
    B: {
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      label: "주의",
      description:
        "선순위 가처분, 대항력 있는 임차인(미배당) 등 인수 권리 존재 가능성.",
    },
    C: {
      color: "text-red-600",
      bgColor: "bg-red-50",
      label: "위험",
      description:
        "유치권, 법정지상권 등 특수권리가 개입되어 소송 가능성이 높은 물건.",
    },
  };

  const currentGrade = gradeConfig[safetyGrade];

  // 배당 시뮬레이션용 낙찰가 (userBid 또는 기본값)
  const assumedBidPrice = userBid || 450000000; // 기본값 4.5억

  // 매물별 동적 FAQ 생성
  const generateFAQs = () => {
    interface FAQ {
      question: string;
      answer: string;
      priority: number; // 우선순위 (낮을수록 먼저 표시)
    }

    const faqs: FAQ[] = [];

    // 1. 기본 FAQ: 말소기준권리 (항상 표시)
    faqs.push({
      question: "'말소기준권리'가 뭔가요?",
      answer: `경매에서 권리가 살아남느냐(인수), 죽느냐(소멸)를 가르는 기준선입니다. 이 권리를 포함해 뒤에 있는 권리는 모두 지워져 안전합니다. 본 사건의 말소기준권리는 ${
        baseRightDate
          ? formatDateString(baseRightDate)
          : "확인 필요"
      }에 설정된 ${
        baseRightCreditor
      }입니다.`,
      priority: 1,
    });

    // 2. 임차인 보증금 관련 FAQ
    if (hasCountervailingPower) {
      faqs.push({
        question: "대항력 있는 임차인이 있으면 어떻게 되나요?",
        answer: hasDividendRequest
          ? `대항력 있는 선순위 임차인이 배당요구를 완료하여 낙찰대금에서 보증금을 우선 배당받게 됩니다. 따라서 낙찰자가 인수해야 할 보증금 부담은 '0원'으로 분석됩니다. 다만, 낙찰가가 낮아 미배당금이 발생하면 그 차액을 인수해야 합니다.`
          : `대항력 있는 선순위 임차인이 배당요구를 하지 않아 보증금 전액(${
              (courtDocs.occupants || [])
                .find((occ) => occ.hasCountervailingPower)
                ?.deposit.toLocaleString() || "확인 필요"
            }원)을 낙찰자가 인수해야 합니다. 입찰가 산정 시 이를 반영해야 합니다.`,
        priority: 2,
      });
    }

    // 3. 특수권리 관련 FAQ
    const hasLien = rights.riskFlags.some((flag) => flag.includes("유치권"));
    const hasEasement = rights.riskFlags.some((flag) =>
      flag.includes("법정지상권"),
    );

    if (hasLien) {
      faqs.push({
        question: "유치권이 신고되면 어떻게 되나요?",
        answer: `유치권은 등기부에 등기되지 않아도 점유만으로 성립하는 권리입니다. 유치권자가 신고하면 낙찰 후에도 물건을 점유할 수 있어 명도가 어려워질 수 있습니다. 본 사건은 ${
          safetyGrade === "C"
            ? "유치권 리스크가 높아 주의가 필요합니다."
            : "유치권 신고 내역이 없어 안전합니다."
        }`,
        priority: 3,
      });
    }

    if (hasEasement) {
      faqs.push({
        question: "법정지상권이 있으면 어떻게 되나요?",
        answer: `법정지상권은 건물 소유를 위해 토지를 사용할 수 있는 권리로, 낙찰 후에도 소멸되지 않습니다. 이는 소유권 행사에 제약을 줄 수 있어 ${
          safetyGrade === "C"
            ? "위험 등급으로 분류되었습니다."
            : "주의가 필요합니다."
        }`,
        priority: 3,
      });
    }

    // 4. 명도 난이도 관련 FAQ
    if (rights.evictionRisk >= 3.0) {
      faqs.push({
        question: "명도가 어려울 것 같아요. 어떻게 해야 하나요?",
        answer: `본 물건의 명도 난이도는 '${
          rights.evictionRisk <= 3.5 ? "중" : "상"
        }'으로 분석되었습니다. 예상 명도 비용은 ${rights.evictionCostEstimated.toLocaleString()}원입니다. ${
          hasDividendRequest
            ? "배당을 받는 임차인은 명도확인서가 필요하므로 협조적일 가능성이 높습니다."
            : "명도 협상이 필요하며, 필요시 법원에 명도소송을 제기할 수 있습니다."
        }`,
        priority: 4,
      });
    }

    // 5. 인수 비용 관련 FAQ
    if (rights.assumableRightsTotal > 0) {
      faqs.push({
        question: "낙찰 후 추가로 지불해야 할 금액이 있나요?",
        answer: `네, 있습니다. 등기부상 인수해야 할 권리 금액이 ${rights.assumableRightsTotal.toLocaleString()}원이며, 명도 비용 ${rights.evictionCostEstimated.toLocaleString()}원을 합하면 총 ${totalAssumableAmount.toLocaleString()}원의 추가 비용이 발생할 수 있습니다. 입찰가 산정 시 이를 반드시 고려해야 합니다.`,
        priority: 5,
      });
    } else {
      faqs.push({
        question: "낙찰 후 추가로 지불해야 할 금액이 있나요?",
        answer: `아니요, 인수해야 할 권리가 없어 추가 비용이 발생하지 않습니다. 다만, 명도 비용 ${
          rights.evictionCostEstimated > 0
            ? rights.evictionCostEstimated.toLocaleString() + "원"
            : "은 없을 것으로 예상됩니다"
        } 정도는 고려해야 할 수 있습니다.`,
        priority: 5,
      });
    }

    // 5.5. 청구금액 관련 FAQ
    if (claimAmount > 0) {
      faqs.push({
        question: "청구금액이 있으면 어떻게 되나요?",
        answer: `배당요구 등으로 ${claimAmount.toLocaleString()}원의 청구금액이 발생합니다. 이는 배당을 요구한 총 금액으로, 낙찰대금 범위 내에서만 실제 배당이 이루어집니다. 초과분(미배당금)은 권리자가 인수하거나 포기하므로, 배당 시뮬레이션을 통해 실제 인수 금액을 확인하세요. 청구금액이 높다고 무조건 부담금액이 증가하는 것은 아닙니다.`,
        priority: 6,
      });
    }

    // 6. 등급별 맞춤 FAQ
    if (safetyGrade === "C") {
      faqs.push({
        question: "C등급 물건은 위험한가요?",
        answer: `C등급은 특수권리(유치권, 법정지상권)나 높은 리스크가 있는 물건입니다. 초보자보다는 경험이 있는 투자자에게 적합하며, 법률 전문가(변호사, 법무사)의 자문을 받는 것을 강력히 권장합니다.`,
        priority: 6,
      });
    } else if (safetyGrade === "B") {
      faqs.push({
        question: "B등급 물건은 입찰해도 될까요?",
        answer: `B등급은 주의가 필요한 물건입니다. 대항력 있는 임차인이나 중간 수준의 리스크가 있지만, 충분한 조사와 입찰가 산정 시 이를 반영한다면 입찰 가능합니다.`,
        priority: 6,
      });
    }

    // 7. 물건 종류별 FAQ
    if (
      property.type === PropertyType.Apartment ||
      property.type === PropertyType.Officetel
    ) {
      faqs.push({
        question: `${getPropertyTypeLabel(
          property.type,
        )}의 경우 특별히 주의할 점이 있나요?`,
        answer: `${getPropertyTypeLabel(
          property.type,
        )}은 관리비 미납이 인수 대상일 수 있습니다. 입찰 전 관리사무소에서 미납 관리비 정확한 금액을 확인해야 합니다. 또한 전입일자와 확정일자 여부를 꼼꼼히 확인해야 합니다.`,
        priority: 7,
      });
    }

    // 8. 배당 관련 FAQ (배당 시뮬레이션이 있는 경우)
    if (allocationTable && allocationTable.rows.length > 0) {
      const hasUnpaidDividend = allocationTable.rows.some(
        (row) =>
          row.willExtinguish !== "소멸" &&
          row.amount - row.expectedAllocation > 0,
      );

      if (hasUnpaidDividend) {
        faqs.push({
          question: "배당에서 못 받은 금액은 누가 부담하나요?",
          answer: `배당 시뮬레이션 결과, 일부 권리자가 배당에서 전액을 받지 못하는 경우가 있습니다. 이 경우 낙찰자가 그 미배당금을 인수해야 합니다. 본 사건의 경우 배당표를 참고하여 정확한 인수 금액을 확인하시기 바랍니다.`,
          priority: 8,
        });
      }
    }

    // 우선순위 순으로 정렬하고 최대 4개까지만 표시
    return faqs.sort((a, b) => a.priority - b.priority).slice(0, 4);
  };

  const dynamicFAQs = generateFAQs();

  // 특수권리 점검 항목 동적 생성
  const generateSpecialRightsChecks = () => {
    interface SpecialRightCheck {
      icon: string;
      title: string;
      description: string;
      status: "안전" | "주의" | "위험" | "확인필요";
      statusColor: string;
    }

    const checks: SpecialRightCheck[] = [];

    // 1. 유치권 신고
    const hasLien = rights.riskFlags.some((flag) => flag.includes("유치권"));
    checks.push({
      icon: "🏗️",
      title: "유치권 신고",
      description: hasLien
        ? "유치권 신고 내역이 확인되었습니다. 유치권자는 점유만으로 권리를 주장할 수 있어 명도가 어려울 수 있습니다. 법률 전문가 자문이 필요합니다."
        : "신고 내역 없음. (현황조사서상 점유관계 확인 결과 특이사항 없음)",
      status: hasLien ? "위험" : "안전",
      statusColor: hasLien
        ? "text-red-600 dark:text-red-400"
        : "text-green-600 dark:text-green-400",
    });

    // 2. 법정지상권
    const hasEasement = rights.riskFlags.some((flag) =>
      flag.includes("법정지상권"),
    );
    if (hasEasement) {
      checks.push({
        icon: "🏛️",
        title: "법정지상권",
        description:
          "법정지상권이 확인되었습니다. 이는 낙찰 후에도 소멸되지 않아 소유권 행사에 제약이 있을 수 있습니다.",
        status: "위험",
        statusColor: "text-red-600 dark:text-red-400",
      });
    }

    // 3. 미납 관리비 (아파트, 오피스텔, 상가 등 공용시설이 있는 경우만)
    const hasCommonArea =
      property.type === PropertyType.Apartment ||
      property.type === PropertyType.Officetel ||
      property.type === PropertyType.Store ||
      property.type === PropertyType.Office ||
      property.type === PropertyType.Factory ||
      property.type === PropertyType.Warehouse;
    if (hasCommonArea) {
      checks.push({
        icon: "🧾",
        title: "미납 관리비",
        description: `공용부분 관리비는 낙찰자 승계 원칙이므로 입찰가 산정 시 고려해야 합니다. ${getPropertyTypeLabel(
          property.type,
        )}의 경우 관리사무소에서 정확한 미납 금액을 확인하시기 바랍니다.`,
        status: "확인필요",
        statusColor: "text-orange-500 dark:text-orange-400",
      });
    }

    // 4. 대지권 (토지가 있는 경우)
    if (property.landSizeM2 && property.landSizeM2 > 0) {
      const hasLandRightsIssue = rights.riskFlags.some(
        (flag) => flag.includes("대지권") || flag.includes("지상권"),
      );
      if (hasLandRightsIssue) {
        checks.push({
          icon: "🗺️",
          title: "대지권/지상권",
          description:
            "대지권 또는 지상권 관련 이슈가 확인되었습니다. 토지 사용권에 대한 추가 확인이 필요합니다.",
          status: "주의",
          statusColor: "text-yellow-600 dark:text-yellow-400",
        });
      }
    }

    return checks;
  };

  const specialRightsChecks = generateSpecialRightsChecks();

  // 입찰 전 행동 가이드 체크리스트 동적 생성
  const generateActionChecklist = () => {
    interface ChecklistItem {
      icon: string;
      title: string;
      description: string;
    }

    const items: ChecklistItem[] = [];

    // 기본 항목: 점유자 확인 (항상 표시)
    items.push({
      icon: "👀",
      title: "점유자 확인",
      description: hasCountervailingPower
        ? `대항력 있는 임차인(${
            (courtDocs.occupants || []).find(
              (occ) => occ.hasCountervailingPower,
            )?.name || "확인 필요"
          })이 거주 중입니다. 배당요구 ${
            hasDividendRequest ? "완료" : "미완료"
          } 상태이므로 명도 협상 전략을 수립해야 합니다. 우편함과 현관문 상태를 확인하여 실제 거주 여부를 파악하세요.`
        : "현재 누가 살고 있는지 벨을 눌러 확인. 우편함에 우편물이 쌓여있는지 체크 (공실 여부 파악).",
    });

    // 물건 종류별 체크리스트
    if (
      property.type === PropertyType.Apartment ||
      property.type === PropertyType.Officetel ||
      property.type === PropertyType.Store ||
      property.type === PropertyType.Office ||
      property.type === PropertyType.Factory ||
      property.type === PropertyType.Warehouse
    ) {
      // 아파트/오피스텔/상가: 물리적 하자 + 관리사무소
      items.push({
        icon: "💧",
        title: "물리적 하자",
        description:
          property.type === PropertyType.Apartment ||
          property.type === PropertyType.Officetel
            ? "아랫집을 방문하여 누수 여부 확인. 외벽 균열 및 샷시 상태 등 수리비 견적 산출. 특히 배수구와 화장실 누수 여부를 중점적으로 확인하세요."
            : "상가의 경우 전기 배선, 가스 배관, 환기 시스템 등을 확인. 인접 상가와의 경계벽 상태도 점검하세요.",
      });

      items.push({
        icon: "🏢",
        title: "관리사무소",
        description: `미납 관리비 정확한 금액 확인. 최근 이사 나간 내역이 있는지 전출입 장부 확인. ${
          property.type === PropertyType.Apartment
            ? "공용전기료, 난방비, 수도료 등도 확인하세요."
            : "상가의 경우 공용전기료와 공용관리비를 구분하여 확인하세요."
        }`,
      });
    } else if (
      property.type === PropertyType.ResidentialLand ||
      property.type === PropertyType.CommercialLand
    ) {
      // 토지: 경계 확인 + 용도지역 확인
      items.push({
        icon: "📐",
        title: "경계 및 지목 확인",
        description:
          "실제 경계석 위치와 등기부등본의 지적도 대조. 인접지와의 경계 분쟁 여부 확인. 지목 변경 가능성도 검토하세요.",
      });

      items.push({
        icon: "🗺️",
        title: "용도지역 및 건축제한",
        description:
          "도시계획상 용도지역 확인. 건폐율, 용적률 제한 확인. 개발제한구역, 보존지역 여부 확인. 향후 개발 계획이 있는지 지자체에 문의하세요.",
      });
    } else {
      // 기타: 기본 물리적 하자만
      items.push({
        icon: "💧",
        title: "물리적 하자",
        description:
          "건물의 구조적 하자, 외벽 균열, 지붕 상태 등을 확인. 수리비 견적 산출.",
      });
    }

    // 명도 난이도가 높은 경우 추가 가이드
    if (rights.evictionRisk >= 3.0) {
      items.push({
        icon: "⚖️",
        title: "명도 전략 수립",
        description: `명도 난이도가 '${
          rights.evictionRisk <= 3.5 ? "중" : "상"
        }'으로 분석되었습니다. 예상 명도 비용은 ${rights.evictionCostEstimated.toLocaleString()}원입니다. ${
          hasDividendRequest
            ? "배당을 받는 임차인은 협조적일 가능성이 높지만,"
            : ""
        } 명도소송 계획을 수립하고 변호사와 상담하시기 바랍니다.`,
      });
    }

    // 인수 비용이 큰 경우 추가 가이드
    if (rights.assumableRightsTotal > 20000000) {
      items.push({
        icon: "💰",
        title: "인수 비용 확인",
        description: `등기부상 인수해야 할 권리 금액이 ${rights.assumableRightsTotal.toLocaleString()}원입니다. 입찰가 산정 시 반드시 고려해야 하며, 실제 인수 비용이 더 클 수 있으므로 여유를 두고 계산하세요.`,
      });
    }

    // 청구금액이 있는 경우 추가 가이드
    if (claimAmount > 0) {
      items.push({
        icon: "📋",
        title: "청구금액 확인",
        description: `배당요구 등으로 ${claimAmount.toLocaleString()}원의 청구금액이 발생합니다. 배당 과정에서 낙찰대금 범위 내에서만 배당되며, 미배당금은 권리자가 인수하므로 배당 시뮬레이션을 확인하세요.`,
      });
    }

    // 최대 4개까지만 표시 (우선순위: 기본 항목 > 물건 종류별 > 특수 상황)
    return items.slice(0, 4);
  };

  const actionChecklist = generateActionChecklist();

  return (
    <div
      ref={containerRef}
      className="bg-gray-200 dark:bg-gray-900 select-none"
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
    >
      {/* ================= PAGE 1: 종합 진단 개요 ================= */}
      <div className="w-full p-3 sm:p-4 md:p-6 bg-white dark:bg-slate-800 rounded-lg shadow-lg mb-4 sm:mb-6">
        {/* Header */}
        <div className="border-b-2 border-slate-800 pb-3 sm:pb-4 mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 sm:gap-0">
          <div className="flex-1 min-w-0">
            <p className="text-[9px] sm:text-[10px] md:text-xs font-bold text-slate-500 tracking-widest mb-1">
              PREMIUM LEGAL REPORT
            </p>
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-[var(--font-nanum-myeongjo)] font-extrabold text-slate-900 dark:text-slate-100 break-words">
              권리분석 종합 진단서
            </h1>
          </div>
          <div className="text-left sm:text-right flex-shrink-0">
            <p className="text-[10px] sm:text-xs md:text-sm font-bold text-slate-800 dark:text-slate-200 break-all">
              사건번호: {courtDocs.caseNumber}
            </p>
            <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
              분석일: {analysisDate}
            </p>
          </div>
        </div>

        {/* 1. 물건 권리 요약 */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-[10px] sm:text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 border-l-4 border-slate-900 dark:border-slate-100 pl-2 mb-2 sm:mb-3">
            1. 물건 권리 요약
          </h3>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-[9px] sm:text-[10px] md:text-xs border-collapse border border-slate-300 dark:border-slate-700 min-w-[400px]">
              <tbody className="text-slate-700 dark:text-slate-300">
                <tr>
                  <td className="bg-slate-100 dark:bg-slate-800 p-2 border border-slate-300 dark:border-slate-700 font-bold w-1/6 text-center">
                    소재지
                  </td>
                  <td
                    className="p-2 border border-slate-300 dark:border-slate-700 w-5/6"
                    colSpan={3}
                  >
                    {property.address}
                  </td>
                </tr>
                <tr>
                  <td className="bg-slate-100 dark:bg-slate-800 p-2 border border-slate-300 dark:border-slate-700 font-bold text-center">
                    건물/토지
                  </td>
                  <td className="p-2 border border-slate-300 dark:border-slate-700">
                    전용 {property.sizeM2.toFixed(2)}㎡
                    {property.landSizeM2
                      ? ` / 대지권 ${property.landSizeM2.toFixed(2)}㎡`
                      : ""}
                  </td>
                  <td className="bg-slate-100 dark:bg-slate-800 p-2 border border-slate-300 dark:border-slate-700 font-bold text-center">
                    물건종별
                  </td>
                  <td className="p-2 border border-slate-300 dark:border-slate-700">
                    {getPropertyTypeLabel(property.type)}
                  </td>
                </tr>
                <tr>
                  <td className="bg-slate-100 dark:bg-slate-800 p-2 border border-slate-300 dark:border-slate-700 font-bold text-center">
                    말소기준
                  </td>
                  <td className="p-2 border border-slate-300 dark:border-slate-700 text-blue-700 dark:text-blue-400 font-bold">
                    {baseRightDate
                      ? formatDateString(baseRightDate)
                      : "없음"}
                  </td>
                  <td className="bg-slate-100 dark:bg-slate-800 p-2 border border-slate-300 dark:border-slate-700 font-bold text-center">
                    배당종기
                  </td>
                  <td className="p-2 border border-slate-300 dark:border-slate-700">
                    {baseRightDate
                      ? formatDateString(baseRightDate)
                      : "확인 필요"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 2. 종합 등급 및 코멘트 */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-5 min-h-[200px] sm:h-48">
          {/* Score */}
          <div className="w-full sm:w-1/3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-3 sm:p-4 flex flex-col items-center justify-center text-center">
            <div className="relative w-24 h-24 rounded-full border-8 border-blue-100 dark:border-blue-900 flex items-center justify-center mb-2">
              <div
                className={`text-2xl sm:text-3xl font-black ${currentGrade.color}`}
              >
                {safetyGrade}
              </div>
            </div>
            <p className="font-bold text-slate-800 dark:text-slate-200 text-xs sm:text-sm">
              권리 안전성: {safetyGrade}등급
            </p>
            <p className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-1 leading-tight">
              * 법적 인수사항{" "}
              {rights.assumableRightsTotal === 0 ? "없음" : "있음"}
              <br />* 명도 난이도 {rights.evictionRisk <= 2.0 ? "낮음" : "높음"}
            </p>
          </div>

          {/* Comment */}
          <div className="w-full sm:w-2/3 text-[10px] sm:text-xs md:text-sm leading-relaxed text-justify text-slate-700 dark:text-slate-300 flex flex-col justify-between">
            <div>
              <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-2 border-b pb-1">
                📋 종합 분석 코멘트
              </h4>
              <p className="mb-2">
                본 사건은 경매의 <strong>[권리 처리 원칙]</strong>에 따라{" "}
                {rights.assumableRightsTotal === 0
                  ? "인수 부담이 전혀 없는 완벽한 안전물건"
                  : `인수해야 할 권리금액 ${rights.assumableRightsTotal.toLocaleString()}원이 있는`}
                으로 분석됩니다. 등기부상 최선순위 {baseRightTypeLabel}(
                {baseRightCreditor}, 말소기준권리) 이전에 설정된 권리는 낙찰 시
                직권 말소되나, 이후 설정된 권리는{" "}
                {rights.assumableRightsTotal > 0
                  ? "인수 대상입니다."
                  : "인수 대상 권리가 없습니다."}
              </p>
              <p>
                {hasCountervailingPower && countervailingOccupant ? (
                  <>
                    <strong>
                      선순위 임차인({countervailingOccupant.name}, 보증금{" "}
                      {countervailingOccupant.deposit.toLocaleString()}원)
                    </strong>
                    이 존재하여 주의가 필요해 보이나,{" "}
                    {hasDividendRequest ? (
                      <>
                        배당요구종기일(
                        {baseRightDate
                          ? formatDateString(baseRightDate)
                          : "확인 필요"}
                        ) 내에 적법하게 배당을 신청하였습니다. 따라서
                        낙찰대금에서 보증금을 우선 배당받게 되며,{" "}
                        <span className="bg-yellow-100 dark:bg-yellow-900/30 px-1 font-bold text-slate-900 dark:text-slate-100">
                          낙찰자가 인수해야 할 보증금 부담은 &apos;0원&apos;으로
                          분석됩니다.
                        </span>
                      </>
                    ) : (
                      <>
                        배당요구가 없어 보증금 전액(
                        {countervailingOccupant.deposit.toLocaleString()}원) 인수
                        대상입니다. 입찰가 산정 시 이를 반영해야 합니다.
                      </>
                    )}
                  </>
                ) : (
                  <>
                    선순위 임차인이 없어 권리상 안전합니다.{" "}
                    {rights.evictionCostEstimated > 0
                      ? `명도 비용(${rights.evictionCostEstimated.toLocaleString()}원)만 고려하면 됩니다.`
                      : "명도 비용도 예상되지 않습니다."}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* 3. 핵심 리스크 체크 */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-[10px] sm:text-xs md:text-sm font-bold text-slate-900 dark:text-slate-100 border-l-4 border-slate-900 dark:border-slate-100 pl-2 mb-2 sm:mb-3">
            2. 핵심 리스크 진단
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            <div className="border border-slate-200 dark:border-slate-700 p-2 sm:p-3 rounded bg-white dark:bg-slate-800 flex flex-col justify-center">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 mb-1">
                <span className="font-bold text-[9px] sm:text-[10px] md:text-xs text-slate-600 dark:text-slate-400">
                  등기부 권리 인수
                </span>
                <span
                  className={`font-bold text-[9px] sm:text-[10px] md:text-xs ${
                    rights.assumableRightsTotal === 0
                      ? "text-green-600"
                      : "text-amber-600"
                  }`}
                >
                  {rights.assumableRightsTotal === 0
                    ? `안전 (0건)`
                    : `주의 (${assumableRightsCount}건, ${rights.assumableRightsTotal.toLocaleString()}원)`}
                </span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                {baseRightTypeLabel}({baseRightCreditor}) 이후{" "}
                {subordinatedRightsCount > 0
                  ? `${subordinatedRightsCount}건의 후순위 권리`
                  : "후순위 권리"}
                {rights.assumableRightsTotal === 0
                  ? " 모두 소멸. 승계 사항 없음."
                  : " 중 인수 사항 있음."}
              </p>
            </div>
            <div className="border border-slate-200 dark:border-slate-700 p-2 sm:p-3 rounded bg-white dark:bg-slate-800 flex flex-col justify-center">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 mb-1">
                <span className="font-bold text-[9px] sm:text-[10px] md:text-xs text-slate-600 dark:text-slate-400">
                  임차보증금 인수
                </span>
                <span
                  className={`font-bold text-[9px] sm:text-[10px] md:text-xs ${
                    hasDividendRequest ? "text-green-600" : "text-amber-600"
                  }`}
                >
                  {hasDividendRequest
                    ? "안전 (전액배당)"
                    : countervailingOccupant
                    ? `주의 (${countervailingOccupant.deposit.toLocaleString()}원 인수)`
                    : "안전 (인수 없음)"}
                </span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                {hasCountervailingPower && countervailingOccupant ? (
                  hasDividendRequest ? (
                    <>
                      {countervailingOccupant.name} 배당요구 완료. 낙찰가율 80%
                      이상 시 보증금({countervailingOccupant.deposit.toLocaleString()}원) 전액 변제.
                    </>
                  ) : (
                    <>
                      {countervailingOccupant.name} 배당요구 없음. 보증금(
                      {countervailingOccupant.deposit.toLocaleString()}원) 전액 인수 필요.
                    </>
                  )
                ) : (
                  "대항력 있는 임차인 없음. 보증금 인수 사항 없음."
                )}
              </p>
            </div>
            <div className="border border-slate-200 dark:border-slate-700 p-2 sm:p-3 rounded bg-white dark:bg-slate-800 flex flex-col justify-center">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 mb-1">
                <span className="font-bold text-[9px] sm:text-[10px] md:text-xs text-slate-600 dark:text-slate-400">
                  청구금액 부담
                </span>
                <span
                  className={`font-bold text-[9px] sm:text-[10px] md:text-xs ${
                    claimAmount === 0
                      ? "text-green-600"
                      : "text-blue-600"
                  }`}
                >
                  {claimAmount === 0
                    ? "안전 (0원)"
                    : `주의 (${claimAmount.toLocaleString()}원)`}
                </span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                {claimAmount > 0
                  ? `배당요구 등으로 ${claimAmount.toLocaleString()}원의 청구금액이 발생합니다. 배당 과정에서 낙찰대금 범위 내에서만 배당되며, 미배당금은 권리자가 인수합니다.`
                  : "배당요구 등으로 인한 청구금액이 없습니다."}
              </p>
            </div>
            <div className="border border-slate-200 dark:border-slate-700 p-2 sm:p-3 rounded bg-white dark:bg-slate-800 flex flex-col justify-center">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 mb-1">
                <span className="font-bold text-[9px] sm:text-[10px] md:text-xs text-slate-600 dark:text-slate-400">
                  위반건축물
                </span>
                <span
                  className={`font-bold text-[9px] sm:text-[10px] md:text-xs ${
                    hasBuildingViolation ? "text-red-600" : "text-green-600"
                  }`}
                >
                  {hasBuildingViolation ? "위험 (등재)" : "안전 (미등재)"}
                </span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                {hasBuildingViolation
                  ? "건축물대장상 위반 내용 확인됨. 이행강제금 리스크 있음. 추가 확인 필요."
                  : "건축물대장상 위반 내용 없음. 이행강제금 리스크 없음."}
              </p>
            </div>
            <div className="border border-slate-200 dark:border-slate-700 p-2 sm:p-3 rounded bg-white dark:bg-slate-800 flex flex-col justify-center">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 mb-1">
                <span className="font-bold text-[9px] sm:text-[10px] md:text-xs text-slate-600 dark:text-slate-400">
                  명도 난이도
                </span>
                <span
                  className={`font-bold text-[9px] sm:text-[10px] md:text-xs ${
                    rights.evictionRisk <= 2.0
                      ? "text-blue-600"
                      : rights.evictionRisk <= 3.5
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  {rights.evictionRisk <= 2.0
                    ? "하 (Easy)"
                    : rights.evictionRisk <= 3.5
                    ? "중 (Medium)"
                    : "상 (Hard)"}
                </span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                {hasCountervailingPower && hasDividendRequest
                  ? "배당받는 임차인은 명도확인서가 필요하므로 협조적임."
                  : hasCountervailingPower && !hasDividendRequest
                  ? `명도 협상 필요. 예상 비용: ${rights.evictionCostEstimated.toLocaleString()}원`
                  : rights.evictionCostEstimated > 0
                  ? `명도 비용 예상: ${rights.evictionCostEstimated.toLocaleString()}원`
                  : "명도 비용 예상 없음."}
              </p>
            </div>
          </div>
        </div>

        {/* 4. 권리 분석 등급 가이드 */}
        <div className="mt-auto bg-gray-50 dark:bg-slate-800 p-4 rounded border border-gray-200 dark:border-slate-700">
          <h4 className="text-[10px] sm:text-xs font-bold text-slate-800 dark:text-slate-200 mb-2">
            ℹ️ 참고: 권리 안전 등급 기준표
          </h4>
          <table className="w-full text-[9px] sm:text-[10px] text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400">
                <th className="pb-1 w-12 text-center">등급</th>
                <th className="pb-1 w-20">상태</th>
                <th className="pb-1">설명</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 dark:text-gray-400">
              {Object.entries(gradeConfig).map(([grade, config]) => (
                <tr
                  key={grade}
                  className={
                    grade === safetyGrade
                      ? "border-b border-gray-200 dark:border-slate-600 bg-blue-50 dark:bg-blue-950/20"
                      : "border-b border-gray-200 dark:border-slate-600"
                  }
                >
                  <td className={`py-1 text-center font-bold ${config.color}`}>
                    {grade}
                  </td>
                  <td className="py-1 font-bold">{config.label}</td>
                  <td className="py-1">{config.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-right text-[8px] sm:text-[9px] text-gray-400 dark:text-gray-500 mt-2">
          Page 1 of 3
        </div>
      </div>

      {/* ================= PAGE 2: 상세 분석 및 근거 ================= */}
      <div className="w-full p-3 sm:p-4 md:p-6 bg-white dark:bg-slate-800 rounded-lg shadow-lg mb-4 sm:mb-6">
        <div className="border-b border-gray-200 dark:border-slate-700 pb-2 sm:pb-3 mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <h2 className="text-sm sm:text-base md:text-lg font-bold text-slate-800 dark:text-slate-200">
            3. 등기부 및 임차 권리 정밀 분석
          </h2>
          <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 dark:text-gray-500">
            Page 2 / 3
          </span>
        </div>

        {/* 1. 등기부 분석 */}
        <div className="mb-6">
          <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
            ① 부동산 등기부 권리 분석
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center w-10">순위</TableHead>
                  <TableHead className="text-center w-20">접수일</TableHead>
                  <TableHead className="w-20 text-center">등기목적</TableHead>
                  <TableHead>권리 내용 및 소멸 사유</TableHead>
                  <TableHead className="text-center w-14">결과</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // RightsEngine의 extractRightsFromRegistry 로직과 동일하게 모든 등기부 리스트 사용
                  const buildingRights = courtDocs.buildingRegisteredRights ?? [];
                  const landRights = courtDocs.landRegisteredRights ?? [];
                  
                  let allRightsForTable: typeof courtDocs.registeredRights;
                  if (buildingRights.length > 0 || landRights.length > 0) {
                    // 건물/토지 등기부가 있으면 합치기
                    allRightsForTable = [...buildingRights, ...landRights];
                  } else {
                    // 없으면 registeredRights 사용
                    allRightsForTable = courtDocs.registeredRights ?? [];
                  }
                  
                  // 중복 제거: 같은 등기목적과 접수일자로 건물등기와 토지등기가 동시에 등재된 경우 중복 제거
                  // creditor + date + amount + type 조합으로 고유성 판단
                  const seen = new Set<string>();
                  const uniqueRights = allRightsForTable.filter(
                    (right) => {
                      const key = `${right.creditor}_${right.date}_${right.amount}_${right.type}`;
                      if (seen.has(key)) {
                        return false;
                      }
                      seen.add(key);
                      return true;
                    },
                  );

                  return uniqueRights.map((right, index) => {
                    // 말소기준권리 판단: baseRightDate와 일치하는 권리 (권리분석 리포트의 기준 날짜와 일치)
                    const isBaseRightByDate = right.date === baseRightDate;
                    
                    // 인수/소멸 여부 판단 (TabRights.tsx의 getRightInheritanceStatus 로직과 동일)
                    let inheritanceStatus: "인수" | "소멸" = "소멸";
                    
                    if (isBaseRightByDate) {
                      // 말소기준권리는 인수
                      inheritanceStatus = "인수";
                    } else if (right.date > baseRightDate) {
                      // 말소기준권리 이후의 권리는 경매로 말소되지 않아 인수해야 함
                      inheritanceStatus = "인수";
                    } else {
                      // 말소기준권리 이전의 권리는 breakdown을 확인하여 inheritable 여부 판단
                      const breakdownItem = rights.breakdown.find(
                        (item) => item.type === right.type,
                      );

                      if (breakdownItem) {
                        inheritanceStatus =
                          breakdownItem.inheritable || breakdownItem.payout > 0
                            ? "인수"
                            : "소멸";
                      } else {
                        // breakdown에 없으면 정책 테이블 확인
                        const policy =
                          RIGHTS_POLICY_TABLE[
                            right.type as keyof typeof RIGHTS_POLICY_TABLE
                          ];
                        inheritanceStatus = policy?.inheritable ? "인수" : "소멸";
                      }
                    }
                    
                    return (
                      <TableRow
                        key={`${right.creditor}_${right.date}_${right.amount}_${index}`}
                        className={
                          isBaseRightByDate
                            ? "bg-blue-50 dark:bg-blue-950/20"
                            : ""
                        }
                      >
                        <TableCell className="text-center">{index + 1}</TableCell>
                        <TableCell className="text-center text-blue-700 dark:text-blue-400 font-bold">
                          {right.date ? formatDateString(right.date) : ""}
                        </TableCell>
                        <TableCell className="text-center font-bold">
                          {getRightTypeLabel(right.type)}
                        </TableCell>
                        <TableCell>
                          {isBaseRightByDate ? (
                            <div>
                              <p className="font-bold text-blue-800 dark:text-blue-300">
                                [말소기준] {right.creditor} (
                                {right.amount.toLocaleString()}원)
                              </p>
                              <p className="mt-1 text-slate-500 dark:text-slate-400 leading-snug text-[10px] sm:text-xs">
                                경매의 기준이 되는 권리입니다. 매각 후 배당받고
                                소멸하며, 이 권리 밑으로는 전부 지워집니다.
                              </p>
                            </div>
                          ) : (
                            <div>
                              <p className="font-bold text-slate-700 dark:text-slate-300">
                                {right.creditor}
                              </p>
                              <p className="mt-1 text-slate-500 dark:text-slate-400 leading-snug text-[10px] sm:text-xs">
                                {inheritanceStatus === "인수"
                                  ? "낙찰자가 인수해야 할 권리입니다."
                                  : "후순위 권리이므로 낙찰자 인수 사항 없음. 직권 말소."}
                              </p>
                            </div>
                          )}
                        </TableCell>
                        <TableCell
                          className={`text-center font-bold ${
                            inheritanceStatus === "인수"
                              ? "text-red-600 dark:text-red-400"
                              : "text-blue-600 dark:text-blue-400"
                          }`}
                        >
                          {inheritanceStatus}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* 2. 임차인 분석 */}
        {courtDocs.occupants && courtDocs.occupants.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
              ② 임차인 대항력 분석
            </h3>
            {courtDocs.occupants.map((occupant, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 p-4 rounded mb-3"
              >
                <div className="grid grid-cols-2 gap-4 mb-3 text-xs sm:text-sm border-b border-slate-100 dark:border-slate-700 pb-3">
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      임차인
                    </span>
                    <span className="font-bold text-base sm:text-lg text-slate-800 dark:text-slate-200">
                      {occupant.name}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      보증금
                    </span>
                    <span className="font-bold text-base sm:text-lg text-slate-800 dark:text-slate-200">
                      {occupant.deposit.toLocaleString()} 원
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      계약일
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {occupant.contractDate
                        ? formatDateString(occupant.contractDate)
                        : "정보 없음"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      확정일자
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {occupant.fixedDate ? (
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {formatDateString(occupant.fixedDate)}
                        </span>
                      ) : (
                        <span className="text-gray-500">미확정</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      전입일
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {occupant.moveInDate
                        ? formatDateString(occupant.moveInDate)
                        : ""}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      대항력
                    </span>
                    <span className={`text-sm font-medium ${occupant.hasCountervailingPower ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {occupant.hasCountervailingPower ? "있음" : "없음"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      배당요구
                    </span>
                    <span className={`text-sm font-medium ${occupant.dividendRequested ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`}>
                      {occupant.dividendRequested ? "있음" : "없음"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
                      우선변제권
                    </span>
                    <span className={`text-sm font-medium ${occupant.hasPriorityRight ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500'}`}>
                      {occupant.hasPriorityRight ? "있음" : "없음"}
                    </span>
                  </div>
                </div>
                <div className="text-[10px] sm:text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                  {occupant.hasCountervailingPower ? (
                    <>
                      <p className="mb-2">
                        <span className="font-bold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-1">
                          Risk Check
                        </span>{" "}
                        계약일({occupant.contractDate ? formatDateString(occupant.contractDate) : "정보 없음"}) +
                        확정일자({occupant.fixedDate ? formatDateString(occupant.fixedDate) : "미확정"}) +
                        전입일({occupant.moveInDate ? formatDateString(occupant.moveInDate) : ""})
                        모두 말소기준권리({baseRightDate ? formatDateString(baseRightDate) : ""})보다 빠르므로 <strong>대항력</strong>이 있습니다.
                      </p>
                      <p>
                        <span className="font-bold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-1">
                          배당 전망
                        </span>{" "}
                        {occupant.dividendRequested ? (
                          <>
                            배당요구를 완료하여 우선적으로 보증금 배당이 가능합니다.
                            {occupant.hasCountervailingPower ? (
                              <> 예상 배당금액은 보증금 전액 수준입니다.</>
                            ) : occupant.hasPriorityRight ? (
                              <> 우선변제권으로 예상 배당금액은 보증금의 90% 수준입니다.</>
                            ) : (
                              <> 배당요구만으로 예상 배당금액은 보증금의 60% 수준입니다.</>
                            )}
                          </>
                        ) : (
                          <>
                            배당요구가 없어 보증금 전액 인수 대상입니다.
                            입찰가 산정 시 보증금 전액을 추가 비용으로 고려해야 합니다.
                          </>
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-2">
                        {occupant.fixedDate ? (
                          <>
                            전입일({occupant.moveInDate ? formatDateString(occupant.moveInDate) : ""}) 또는
                            확정일자({formatDateString(occupant.fixedDate)}) 중 하나 이상이
                            말소기준권리({baseRightDate ? formatDateString(baseRightDate) : ""}) 이후이므로 <strong>대항력이 없습니다</strong>.
                          </>
                        ) : (
                          <>
                            확정일자가 없고 전입일({occupant.moveInDate ? formatDateString(occupant.moveInDate) : ""})이
                            말소기준권리({baseRightDate ? formatDateString(baseRightDate) : ""}) 이후이므로 <strong>대항력이 없습니다</strong>.
                          </>
                        )}
                      </p>
                      <p>
                        <span className="font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-1">
                          배당 전망
                        </span>{" "}
                        {occupant.dividendRequested ? (
                          <>
                            {occupant.hasPriorityRight ? (
                              <>우선변제권으로 보증금의 90% 배당이 가능합니다.</>
                            ) : (
                              <>배당요구로 보증금의 60% 배당이 가능합니다. 대항력이 없어 우선순위가 낮습니다.</>
                            )}
                          </>
                        ) : (
                          <>
                            배당요구가 없어 배당금액이 0원입니다.
                            명도 협상이 상대적으로 수월할 것으로 예상됩니다.
                          </>
                        )}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 3. 배당 시뮬레이션 */}
        {allocationTable && (
          <div className="mb-6 flex-grow">
            <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
              ③ 예상 배당표 (낙찰가 {assumedBidPrice.toLocaleString()}원 가정)
            </h3>
            <p className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400 mb-2">
              낙찰대금에서 각 권리자에게 실제로 배당되는 금액을 확인합니다. 미배당금은 낙찰자가 인수합니다.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>배당순위</TableHead>
                    <TableHead>권리자</TableHead>
                    <TableHead className="text-right">청구금액</TableHead>
                    <TableHead className="text-right bg-blue-50 dark:bg-blue-950/20">
                      배당금액
                    </TableHead>
                    <TableHead className="text-right text-red-600 dark:text-red-400">
                      미배당금 (인수)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocationTable.rows.map((row) => (
                    <TableRow
                      key={row.rank}
                      className={
                        row.hasCountervailingPower
                          ? "bg-green-50 dark:bg-green-950/20"
                          : ""
                      }
                    >
                      <TableCell className="font-bold">
                        {row.rank === 0 ? "0순위" : `${row.rank}순위`}
                      </TableCell>
                      <TableCell className="text-left pl-4 font-bold">
                        {row.creditor}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.amount > 0 ? row.amount.toLocaleString() : "-"}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-700 dark:text-blue-400">
                        {row.expectedAllocation.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-gray-400 dark:text-gray-500">
                        {row.willExtinguish === "소멸"
                          ? "0 (완전변제)"
                          : row.amount - row.expectedAllocation > 0
                          ? (
                              row.amount - row.expectedAllocation
                            ).toLocaleString()
                          : "0"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* 4. 법률 FAQ - 매물별 동적 생성 */}
        <div className="mt-auto bg-slate-100 dark:bg-slate-800 p-4 rounded border border-slate-200 dark:border-slate-700">
          <h4 className="text-[10px] sm:text-xs font-bold text-slate-800 dark:text-slate-200 mb-2">
            ⚖️ 초보자를 위한 법률 FAQ
          </h4>
          <div
            className={`grid ${
              dynamicFAQs.length === 1
                ? "grid-cols-1"
                : dynamicFAQs.length === 2
                ? "grid-cols-2"
                : "grid-cols-2"
            } gap-4 text-[9px] sm:text-[10px] text-slate-600 dark:text-slate-400 leading-snug`}
          >
            {dynamicFAQs.map((faq, index) => (
              <div key={index}>
                <p className="font-bold text-slate-800 dark:text-slate-200 mb-1">
                  Q. {faq.question}
                </p>
                <p>A. {faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-right text-[8px] sm:text-[9px] text-gray-400 dark:text-gray-500 mt-2">
          Page 2 of 3
        </div>
      </div>

      {/* ================= PAGE 3: 최종 점검 및 행동 가이드 ================= */}
      <div className="w-full p-3 sm:p-4 md:p-6 bg-white dark:bg-slate-800 rounded-lg shadow-lg mb-4 sm:mb-6">
        <div className="border-b border-gray-200 dark:border-slate-700 pb-2 sm:pb-3 mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <h2 className="text-sm sm:text-base md:text-lg font-bold text-slate-800 dark:text-slate-200">
            4. 최종 점검 및 행동 가이드
          </h2>
          <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 dark:text-gray-500">
            Page 3 / 3
          </span>
        </div>

        {/* 1. 특수권리 점검 - 매물별 동적 생성 */}
        <div className="mb-6">
          <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">
            ③ 등기 외 특수 권리 점검
          </h3>
          <div className="space-y-3">
            {specialRightsChecks.map((check, index) => (
              <div
                key={index}
                className="flex border border-slate-200 dark:border-slate-700 rounded p-3 items-center"
              >
                <div className="w-8 text-xl text-center">{check.icon}</div>
                <div className="w-32 text-[10px] sm:text-xs font-bold text-slate-700 dark:text-slate-300 border-r px-2">
                  {check.title}
                </div>
                <div className="flex-1 px-3 text-[10px] sm:text-xs text-slate-600 dark:text-slate-400">
                  {check.description}
                </div>
                <div
                  className={`text-[10px] sm:text-xs font-bold ${check.statusColor} px-2`}
                >
                  {check.status}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. 입찰 전 행동 가이드 - 매물별 동적 생성 */}
        <div className="mb-8">
          <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">
            ④ 입찰 전 현장 조사(임장) 체크리스트
          </h3>
          <div
            className={`grid ${
              actionChecklist.length === 1
                ? "grid-cols-1"
                : actionChecklist.length === 2
                ? "grid-cols-2"
                : actionChecklist.length === 3
                ? "grid-cols-3"
                : "grid-cols-2"
            } gap-4`}
          >
            {actionChecklist.map((item, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 p-3 rounded text-center"
              >
                <div className="text-lg mb-1">{item.icon}</div>
                <p className="text-[10px] sm:text-xs font-bold text-slate-800 dark:text-slate-200 mb-1">
                  {item.title}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400 text-left leading-tight">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 3. 최종 결론 */}
        <div className="mb-6 flex-grow">
          <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">
            ⑤ 시스템 최종 진단
          </h3>
          <div className="bg-slate-800 dark:bg-slate-900 text-white p-5 rounded shadow-sm">
            <h4 className="text-xs sm:text-sm font-bold mb-2">
              &quot;권리상{" "}
              {safetyGrade === "S"
                ? "매우 안전한"
                : safetyGrade === "A"
                ? "안전한"
                : safetyGrade === "B"
                ? "주의가 필요한"
                : "위험한"}{" "}
              물건입니다.&quot;
            </h4>
            <p className="text-[10px] sm:text-xs text-slate-300 leading-relaxed text-justify">
              본 물건은 권리 분석상{" "}
              <strong className="text-white">
                낙찰자가 인수해야 할 금전적/법적 하자가{" "}
                {(() => {
                  const totalCost = totalAssumableAmount + claimAmount;

                  if (totalCost === 0) {
                    return "발견되지 않았습니다";
                  }

                  const hasRightsCost = rights.assumableRightsTotal > 0;
                  const hasEvictionCost = rights.evictionCostEstimated > 0;
                  const hasClaimAmount = claimAmount > 0;

                  const costDetails = [];

                  if (hasRightsCost) {
                    costDetails.push(`등기부 권리 ${rights.assumableRightsTotal.toLocaleString()}원`);
                  }
                  if (hasEvictionCost) {
                    costDetails.push(`명도 비용 ${rights.evictionCostEstimated.toLocaleString()}원`);
                  }
                  if (hasClaimAmount) {
                    costDetails.push(`청구금액 ${claimAmount.toLocaleString()}원`);
                  }

                  return `총 ${totalCost.toLocaleString()}원 (${costDetails.join(' + ')}) 발생`;
                })()}
              </strong>
              . {safetyGrade === "S" || safetyGrade === "A" ? "초보" : "중급"}{" "}
              투자자가 접근하기에{" "}
              {safetyGrade === "S" || safetyGrade === "A"
                ? "적합한 난이도"
                : "주의가 필요한 난이도"}
              이며, 시세 조사와 명도 전략 수립에 집중하여 입찰하시기 바랍니다.
            </p>
            <div className="mt-2 text-right text-[8px] sm:text-[9px] text-slate-500">
              System ID: {safetyGrade}-VERIFIED-
              {String(new Date().getFullYear())}
            </div>
          </div>
        </div>

        {/* 4. 면책 조항 */}
        <div className="mt-auto border-t-4 border-gray-300 dark:border-slate-600 pt-4">
          <div className="flex gap-4">
            <div className="w-10 text-2xl text-gray-400 dark:text-gray-500 text-center pt-1">
              ⚖️
            </div>
            <div className="flex-1 text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400 text-justify leading-snug space-y-2">
              <p>
                <strong>1. 학습용 시뮬레이션 고지:</strong> 본 리포트는 공공
                데이터(대법원, 등기소)를 활용하여 시스템이 자동으로 생성한
                &apos;모의 분석 리포트&apos;입니다. 법률 전문가(변호사,
                법무사)의 정식 감정평가나 법률 의견서가 아님을 명시합니다.
              </p>
              <p>
                <strong>2. 정보의 한계 및 변동성:</strong> 본 분석은 작성일(
                {analysisDate}) 기준의 공부상 자료를 토대로 합니다. 작성일 이후
                발생한 권리 변동(유치권 기습 신고, 배당요구 철회, 대지권 미등기
                사유 변경 등)이나 서류상의 오기입에 대해서는 시스템이 책임질 수
                없습니다.
              </p>
              <p>
                <strong>3. 면책 및 책임 제한:</strong> 본 리포트의 내용은 입찰을
                위한 참고 자료일 뿐이며, 투자의 절대적 지표가 될 수 없습니다. 본
                정보를 신뢰하여 행한 입찰 결과에 따른 금전적 손실(보증금 몰수,
                소유권 취득 제한 등)에 대해 서비스 제공자는 민·형사상 어떠한
                책임도 지지 않습니다. 최종 권리 확인 의무는 전적으로 입찰자
                본인에게 있습니다.
              </p>
            </div>
          </div>
          <div className="text-center mt-4 text-[8px] sm:text-[9px] text-gray-400 dark:text-gray-500 font-mono">
            End of Document | Rights Analysis Report | Generated by System
          </div>
        </div>
      </div>
    </div>
  );
}
